/**
 * Business marketing page — ported from `.scratch/bedd-snapshot/business.html`
 * into the bimassess SPA. Mirrors `landing.tsx`'s pattern: markup is
 * `dangerouslySetInnerHTML`'d into a `.bedd-marketing` wrapper; the inline
 * IIFE from the source page (sticky-nav compress, scroll reveals, hero skill-
 * bar reveal, counters, magnetic buttons, final-CTA spotlight, tab toggles,
 * pricing toggle, reduced-motion fallback) is re-run from useEffect after
 * mount. See landing.tsx for the load-bearing rationale.
 */

import { useEffect, useRef } from 'react';
import { markup } from './business-assets/markup';
import './marketing-shared/marketing-shared.css';
import './business-assets/business.css';

export function BusinessPage(): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'For Business · BIMAssess';
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // ─── PRICING TOGGLE (price-toggle button group) ──
    const priceTogglesByGroup = new Map<HTMLElement, HTMLButtonElement[]>();
    const priceToggleHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    wrapper.querySelectorAll<HTMLElement>('.price-toggle').forEach((group) => {
      const btns = Array.from(group.querySelectorAll<HTMLButtonElement>('button'));
      priceTogglesByGroup.set(group, btns);
      btns.forEach((btn) => {
        const handler = (): void => {
          btns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
        };
        btn.addEventListener('click', handler);
        priceToggleHandlers.push({ btn, handler });
      });
    });

    // ─── STICKY NAV COMPRESS ─────────────────────
    const nav = wrapper.querySelector<HTMLElement>('nav.site-nav');
    const onScroll = (): void => {
      if (!nav) return;
      nav.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // ─── SCROLL REVEALS ──────────────────────────
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            revealObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => revealObs.observe(el));

    // ─── HERO SKILL BARS ─────────────────────────
    const heroMockup = wrapper.querySelector<HTMLElement>('.hero-mockup');
    let barObs: IntersectionObserver | null = null;
    if (heroMockup) {
      barObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setTimeout(() => heroMockup.classList.add('bars-animate'), 600);
              barObs?.unobserve(e.target);
            }
          });
        },
        { threshold: 0.3 },
      );
      barObs.observe(heroMockup);
    }

    // ─── ANIMATED COUNTERS ───────────────────────
    const counters = Array.from(wrapper.querySelectorAll<HTMLElement>('.counter'));
    const counterObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const target = e.target as HTMLElement;
          if (e.isIntersecting && !target.dataset.done) {
            target.dataset.done = 'true';
            const targetValue = parseFloat(target.dataset.target ?? '0');
            const decimal = target.dataset.decimal === '1';
            const suffix = target.dataset.suffix ?? '';
            const duration = 1600;
            const start = performance.now();
            const animate = (now: number): void => {
              const progress = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const value = targetValue * eased;
              if (decimal) {
                target.innerHTML = (value / 10).toFixed(1) + suffix;
              } else {
                target.innerHTML = Math.floor(value).toLocaleString() + suffix;
              }
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else if (decimal) {
                target.innerHTML = (targetValue / 10).toFixed(1) + suffix;
              } else {
                target.innerHTML = targetValue.toLocaleString() + suffix;
              }
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.4 },
    );
    counters.forEach((c) => counterObs.observe(c));

    // ─── MAGNETIC BUTTONS ────────────────────────
    const magneticBtns = Array.from(wrapper.querySelectorAll<HTMLElement>('.btn-magnetic'));
    const magneticHandlers = new Map<
      HTMLElement,
      { move: (e: MouseEvent) => void; leave: () => void }
    >();
    magneticBtns.forEach((btn) => {
      const move = (e: MouseEvent): void => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.25}px)`;
      };
      const leave = (): void => {
        btn.style.transform = 'translate(0, 0)';
      };
      magneticHandlers.set(btn, { move, leave });
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', leave);
    });

    // ─── FINAL CTA SPOTLIGHT ─────────────────────
    const ctaSection = wrapper.querySelector<HTMLElement>('.final-cta');
    const ctaMoveHandler = (e: MouseEvent): void => {
      if (!ctaSection) return;
      const rect = ctaSection.getBoundingClientRect();
      ctaSection.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      ctaSection.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    if (ctaSection) {
      ctaSection.addEventListener('mousemove', ctaMoveHandler);
    }

    // ─── TAB TOGGLES (catalogue + pricing tab buttons) ──
    const tabGroupHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    wrapper.querySelectorAll<HTMLElement>('.tab-toggle').forEach((group) => {
      const btns = Array.from(group.querySelectorAll<HTMLButtonElement>('.tab-btn'));
      btns.forEach((btn) => {
        const handler = (): void => {
          btns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
        };
        btn.addEventListener('click', handler);
        tabGroupHandlers.push({ btn, handler });
      });
    });

    // ─── REDUCED MOTION ──────────────────────────
    if (typeof window.matchMedia === 'function') {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduced.matches) {
        wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
          el.classList.add('in-view');
        });
        magneticBtns.forEach((btn) => {
          btn.style.transform = '';
        });
      }
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      revealObs.disconnect();
      barObs?.disconnect();
      counterObs.disconnect();
      if (ctaSection) ctaSection.removeEventListener('mousemove', ctaMoveHandler);
      priceToggleHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
      magneticHandlers.forEach(({ move, leave }, btn) => {
        btn.removeEventListener('mousemove', move);
        btn.removeEventListener('mouseleave', leave);
      });
      tabGroupHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="bedd-marketing bedd-business"
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
